/**
 * @author Brendan Magee
 * @desc component for notification target selection
 * @example <notification-target notification="notification"
 *                               form-submitted="submitted">
 *          </notification-target>
 */
import template from './notificationTarget.html';

function NotificationTargetController(
  $log,
  $translate,
  Integration,
  timezoneService,
  teamsService
) {
  this.$onInit = () => {
    this.slackChannelTypes = [
      {
        id: 'directMessage',
        icon: 'fa-at',
        name: $translate.instant('DIRECT_MESSAGE'),
        placeholder: $translate.instant('ENTER_SLACK_USERNAME')
      },
      {
        id: 'publicChannel',
        icon: 'fa-slack',
        name: $translate.instant('PUBLIC_CHANNEL'),
        placeholder: $translate.instant('ENTER_CHANNEL_NAME')
      },
      {
        id: 'privateChannel',
        icon: 'fa-lock',
        name: $translate.instant('PRIVATE_CHANNEL'),
        placeholder: $translate.instant('ENTER_CHANNEL_NAME')
      }
    ];
    this.teamsChannelTypes = [
      {
        id: 'channel',
        name: $translate.instant('CHANNEL'),
        placeholder: $translate.instant('TEAMS.SELECT_CHANNEL_FROM_SUGGESTIONS')
      },
      {
        id: 'chat',
        name: $translate.instant('TEAMS.CHAT'),
        placeholder: $translate.instant('TEAMS.SELECT_USER_FROM_SUGGESTIONS')
      }
    ];

    this.slackChannelType = 'directMessage';
    this.teamsChannelType = 'channel';
    this.channelValue = '';
    this.timezones = timezoneService.timezones;
    this.currentTimezone = timezoneService.getTimezoneDisplay(
      timezoneService.userTimezone
    );
    this.notification.target.secretsEncrypted = true;
    this.slackIntegrations = [];
    this.teamsIntegrations = [];

    Integration.getAllByType('slack').then(
      (data) => {
        this.slackIntegrations = data || [];

        const hasIntegrations =
          !!this.slackIntegrations.length &&
          angular.isArray(this.slackIntegrations);

        const firstIntegrationId = hasIntegrations
          ? this.slackIntegrations[0].id
          : null;
        this.setSlackIntegration(firstIntegrationId);
        this.notification.target.slackIntegrationID = firstIntegrationId;
      },
      (error) => {
        this.integrationsError = true;
        $log.error('Error getting integrations for notification target', error);
      }
    );
    Integration.getAllByType('teams').then(
      (data) => {
        this.teamsIntegrations = data || [];

        const hasIntegrations =
          !!this.teamsIntegrations.length &&
          angular.isArray(this.teamsIntegrations);

        const firstIntegrationId = hasIntegrations
          ? this.teamsIntegrations[0].id
          : null;
        this.setTeamsIntegration(firstIntegrationId);
        this.notification.target.teamsIntegrationID = firstIntegrationId;
      },
      (error) => {
        this.integrationsError = true;
        $log.error('Error getting integrations for notification target', error);
      }
    );
  };

  let setInitial = true;

  this.$doCheck = () => {
    const target = { ...this.notification.target };

    if (
      target.type === 'slack' ||
      target.type === 'email' ||
      target.type === 'teams'
    ) {
      const zone =
        this.notification.target.timezone || timezoneService.userTimezone;
      this.notification.target.timezone = zone;
      this.currentTimezone = timezoneService.getTimezoneDisplay(zone);
    }

    if ((target.channel || target.userId) && setInitial) {
      if (target.type === 'slack') {
        this.channelValue = target.channel;

        if (target.channel.startsWith('@')) {
          this.slackChannelType = 'directMessage';
          this.channelValue = target.channel.slice(1);
        } else if (target.channel.startsWith('#')) {
          this.slackChannelType = 'publicChannel';
          this.channelValue = target.channel.slice(1);
        } else {
          this.slackChannelType = 'privateChannel';
        }
      } else if (target.type === 'teams') {
        this.teamsChannel = target.displayName.split(' / ')[1];
        if (target.userId) this.teamsChannelType = 'chat';
      }
      setInitial = false;
    }

    if (this.slackChannelType && target.type === 'slack') {
      let currentChannelType = this.slackChannelTypes.find(
        (type) => type.id === this.slackChannelType
      );
      if (!currentChannelType) currentChannelType = this.slackChannelTypes[0];

      this.slackIcon = currentChannelType.icon;
      this.placeholderText = currentChannelType.placeholder;
      this.notification.target.slackChannelType = currentChannelType.id;
    }

    if (this.teamsChannelType && target.type === 'teams') {
      let currentChannelType = this.teamsChannelTypes.find(
        (type) => type.id === this.teamsChannelType
      );
      if (!currentChannelType) currentChannelType = this.teamsChannelTypes[0];

      this.placeholderText = currentChannelType.placeholder;
      this.notification.target.teamsChannelType = currentChannelType.id;
    }

    if (
      target.type === 'slack' &&
      this.channelValue !== undefined &&
      this.channelValue !== null
    ) {
      let prefix = '';
      if (this.slackChannelType === 'directMessage') {
        prefix = '@';
      } else if (this.slackChannelType === 'publicChannel') {
        prefix = '#';
      }
      this.notification.target.channel = this.channelValue
        ? `${prefix}${this.channelValue}`
        : '';
      this.notification.target.username =
        this.slackChannelType === 'directMessage'
          ? this.notification.target.channel
          : null;
    }

    if (target.type === 'slack') {
      if (
        this.selectedSlackIntegration &&
        this.slackChannelType !== 'directMessage'
      ) {
        this.slackData = {
          botName: this.selectedSlackIntegration.botName,
          slackChannel: this.notification.target.channel
        };
      }
      if (target.integrationID && this.slackIntegrations) {
        //  set the selected integration when it becomes available on page load
        this.setSlackIntegration(target.integrationID);
        this.notification.target.slackIntegrationID = target.integrationID;
        this.notification.target.integrationID = null;
      }
    } else if (target.type === 'teams') {
      if (this.selectedTeamsIntegration && this.teamsChannelType !== 'chat') {
        this.teamsData = {
          teamsChannel: this.notification.target.channel
        };
      }
      if (target.integrationID && this.teamsIntegrations) {
        //  set the selected integration when it becomes available on page load
        this.setTeamsIntegration(target.integrationID);
        this.notification.target.teamsIntegrationID = target.integrationID;
        this.notification.target.integrationID = null;
      }
    }
  };

  this.selectTeamsChannel = (item) => {
    if (this.teamsChannelType === 'chat') {
      this.notification.target.userId = item.id;
      delete this.notification.target.channelId;
    } else {
      this.notification.target.channelId = item.id;
      delete this.notification.target.userId;
    }
    this.notification.target.displayName = `${this.selectedTeamsIntegration.teamName} / ${item.name}`;
  };

  this.setTeamsLists = async () => {
    this.teamsChannelList = await teamsService.getChannelList(
      this.selectedTeamsIntegration.id
    );
    const userList = await teamsService.getUserList(
      this.selectedTeamsIntegration.id
    );
    this.teamsUserList = userList.map(({ id, givenName, surname }) => ({
      id,
      name: $translate.instant('NAME_ORDER', { givenName, surname })
    }));
  };

  this.filterChannelsOrUsers = async (substr) => {
    let list;
    const setList = () => {
      if (this.teamsChannelType === 'chat') {
        list = this.teamsUserList;
      } else {
        list = this.teamsChannelList;
      }
    };
    setList();

    if (!list) {
      await this.setTeamsLists();
      setList();
    }
    const ret = list.filter((item) =>
      item.name.toUpperCase().includes(substr.toUpperCase())
    );
    return ret;
  };

  this.setSlackIntegration = (integrationID) => {
    this.selectedSlackIntegration = this.slackIntegrations.find(
      (int) => int.id === integrationID
    ) || { botName: $translate.instant('UNKNOWN') };
  };

  this.setTeamsIntegration = (integrationID, shouldClearChannel) => {
    const newTeamsIntegration = this.teamsIntegrations.find(
      (int) => int.id === integrationID
    );
    if (newTeamsIntegration !== this.selectedTeamsIntegration) {
      this.selectedTeamsIntegration = newTeamsIntegration;
      this.setTeamsLists();
      if (shouldClearChannel) this.teamsChannel = '';
    }
  };

  this.resetChannel = () => {
    this.teamsChannel = '';
  };

  let originalSecret = null;

  this.updateSecret = () => {
    this.updatedSecret = true;
    originalSecret = this.notification.target.authToken;
    this.notification.target.authToken = null;
    this.notification.target.secretsEncrypted = false;
  };

  this.cancel = () => {
    this.updatedSecret = false;
    this.notification.target.authToken = originalSecret;
    this.notification.target.secretsEncrypted = true;
  };
}

const notificationTarget = {
  template,
  bindings: {
    notification: '=',
    formSubmitted: '=',
    usernameError: '<'
  },
  require: {
    parent: '^form'
  },
  controller: 'NotificationTargetController'
};

const controllerArray = [
  '$log',
  '$translate',
  'Integration',
  'timezoneService',
  'teamsService',
  NotificationTargetController
];

export default function (ngModule) {
  ngModule.component('notificationTarget', notificationTarget);
  ngModule.controller('NotificationTargetController', controllerArray);
}
